import { evidenceApi } from './evidence.ts';

/**
 * The evidence-workspace client — cases, disclosure and redaction, the three
 * deferred surfaces layered on top of the read-shaped Evidence module
 * (`lib/evidence.ts`).
 *
 * Every call goes through the same token seam as `lib/evidence.ts`: the
 * dashboard exchanges its EVAC session for a ten-minute, org-scoped ES256
 * token and talks to the separately-scoped evidence service with it. So the
 * same three refusals are first-class here too and callers branch on them
 * exactly as the Evidence screen does:
 *   - ApiError 501 from the exchange     → the seam is off in this environment.
 *   - ApiError 403 "evidence not enabled" → onboarding (turn it on on Evidence).
 *   - EvidenceReasonRequired (service 428) → the reason-for-access prompt; the
 *     stated reason then travels with every workspace read this session.
 *
 * These functions add nothing to the seam — they name the exact routes the
 * evidence service serves (`evac-evidence-service` `src/evidence/workspace-routes.ts`
 * and `src/redaction/routes.ts`) so the contract lives in one place.
 */

/* -------------------------------------------------------------------- cases */

export interface CaseRow {
  id: string;
  tenant_id: string;
  external_ref: string | null;
  title: string;
  state: 'open' | 'closed' | 'archived';
  created_by: string;
  created_at: string;
}

export type CaseListRow = CaseRow & { linked_count: number };

/** An asset currently linked into a case (service `cases.ts` LinkedAsset). */
export interface LinkedAsset {
  asset_id: string;
  linked_by: string;
  linked_at: string;
  state: string;
  media_type: string;
  merkle_root: string;
  captured_at: string;
}

export interface CaseDetail {
  case: CaseRow;
  assets: LinkedAsset[];
}

/** GET /v1/cases — tenant-scoped, newest first, each with a live-link count. */
export const listCases = (orgId: string) =>
  evidenceApi<{ items: CaseListRow[]; next_cursor: string | null }>(orgId, '/v1/cases');

/** GET /v1/cases/:id — the case plus its currently-linked assets. */
export const getCase = (orgId: string, id: string) =>
  evidenceApi<CaseDetail>(orgId, `/v1/cases/${id}`);

/** POST /v1/cases — create a case (custodian / org_admin). */
export const createCase = (orgId: string, body: { title: string; externalRef?: string }) =>
  evidenceApi<CaseRow>(orgId, '/v1/cases', {
    method: 'POST',
    body: JSON.stringify({
      title: body.title,
      ...(body.externalRef ? { externalRef: body.externalRef } : {}),
    }),
  });

/**
 * POST /v1/cases/:id/assets — link an asset. Idempotent server-side: relinking
 * an already-linked asset returns `{ linked: false }` and writes no second
 * custody event.
 */
export const linkAsset = (orgId: string, caseId: string, assetId: string) =>
  evidenceApi<{ linked: boolean }>(orgId, `/v1/cases/${caseId}/assets`, {
    method: 'POST',
    body: JSON.stringify({ assetId }),
  });

/** DELETE /v1/cases/:id/assets/:assetId — unlink (idempotent, reversible). */
export const unlinkAsset = (orgId: string, caseId: string, assetId: string) =>
  evidenceApi<{ unlinked: boolean }>(orgId, `/v1/cases/${caseId}/assets/${assetId}`, {
    method: 'DELETE',
  });

/* --------------------------------------------------------------- disclosure */

export interface DisclosureRow {
  id: string;
  tenant_id: string;
  recipient: string;
  purpose: string;
  method: string;
  case_id: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revocation_reason: string | null;
  manifest_id: string;
  disclosed_by: string;
  created_at: string;
}

export type DisclosureListRow = DisclosureRow & { item_count: number };

export interface DisclosureItem {
  asset_id: string;
  delivered_sha256: string;
  derivative_of: string | null;
}

export interface CreatedDisclosure {
  disclosure: DisclosureRow;
  items: DisclosureItem[];
  receipt: {
    canonical: Record<string, unknown>;
    manifest_hash: string;
    signature: { role: string; alg: string; key_id: string; sig: string };
  };
}

export interface DisclosureDetail {
  disclosure: DisclosureRow;
  items: DisclosureItem[];
}

/**
 * The affidavit-ready sealed bundle (service `disclosure.ts` SealedBundle).
 * Only the fields the dashboard surfaces are named; the whole object is what
 * gets downloaded and verified offline, so the rest rides along untyped.
 */
export interface DisclosureCertificate {
  title: string;
  integrity_basis: string;
  transparency_log: {
    verdict_ceiling: string;
    witnessed: boolean;
    tree_size: number;
    root_hash: string;
  };
  statements: string[];
  [k: string]: unknown;
}

export interface SealedBundle {
  kind: string;
  disclosure_id: string;
  generated_at: string;
  certificate: DisclosureCertificate;
  verifier: { verdict_ceiling: string; note: string };
  [k: string]: unknown;
}

/** GET /v1/disclosures — tenant-scoped, newest first, each with an item count. */
export const listDisclosures = (orgId: string) =>
  evidenceApi<{ items: DisclosureListRow[]; next_cursor: string | null }>(orgId, '/v1/disclosures');

/** GET /v1/disclosures/:id — the disclosure and its items. */
export const getDisclosure = (orgId: string, id: string) =>
  evidenceApi<DisclosureDetail>(orgId, `/v1/disclosures/${id}`);

export interface CreateDisclosureBody {
  recipient: string;
  purpose: string;
  method: string;
  /** Provide exactly one source: a case, or a non-empty set of asset ids. */
  caseId?: string;
  assetIds?: string[];
  expiresAt?: string;
}

/** POST /v1/disclosures — seal a disclosure (disclosure_officer / org_admin). */
export const createDisclosure = (orgId: string, body: CreateDisclosureBody) =>
  evidenceApi<CreatedDisclosure>(orgId, '/v1/disclosures', {
    method: 'POST',
    body: JSON.stringify({
      recipient: body.recipient,
      purpose: body.purpose,
      method: body.method,
      ...(body.caseId ? { caseId: body.caseId } : {}),
      ...(body.assetIds && body.assetIds.length > 0 ? { assetIds: body.assetIds } : {}),
      ...(body.expiresAt ? { expiresAt: body.expiresAt } : {}),
    }),
  });

/** GET /v1/disclosures/:id/bundle — the sealed, offline-verifiable bundle. */
export const getDisclosureBundle = (orgId: string, id: string) =>
  evidenceApi<SealedBundle>(orgId, `/v1/disclosures/${id}/bundle`);

/**
 * POST /v1/disclosures/:id/revoke — revoke, once. Irreversible: it writes a
 * `disclosure_revoked` custody event on every disclosed asset and stamps the
 * register. A repeat call is a no-op that returns the already-revoked row.
 */
export const revokeDisclosure = (orgId: string, id: string, reason?: string) =>
  evidenceApi<DisclosureRow>(orgId, `/v1/disclosures/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });

/* ---------------------------------------------------------------- redaction */

/**
 * A canonical redaction region (service `redaction/regions.ts`): a time span in
 * milliseconds relative to the parent's timeline and a normalised 0..1 box.
 */
export interface RedactionRegionInput {
  startMs: number;
  endMs: number;
  box: { x: number; y: number; w: number; h: number };
}

/** The derivative the redaction route returns (service `redaction/routes.ts`). */
export interface RedactionResult {
  derivative_asset_id: string;
  parent_asset_id: string;
  kind: string;
  method: string;
  /** TRUE only when pixels were actually altered. The NullAdapter: false. */
  burned_in: boolean;
  engine: string;
  engine_version: string;
  model_version: string | null;
  region_count: number;
  region_set_hash: string;
  merkle_root: string;
  manifest_hash: string;
  job_id: string;
  version: number;
  cohort_key_id: string | null;
  /**
   * The service's own honest note, present verbatim only when nothing was
   * burned in: "regions declared, not yet burned-in — no pixels altered
   * (demo NullAdapter)". Surfaced, never paraphrased.
   */
  note?: string;
}

/**
 * POST /v1/assets/:id/redact — create a redaction derivative of a stored asset
 * (custodian / redactor). The demo NullAdapter records the regions as a signed
 * derivative declaration and alters no pixels.
 */
export const createRedaction = (orgId: string, assetId: string, regions: RedactionRegionInput[]) =>
  evidenceApi<RedactionResult>(orgId, `/v1/assets/${assetId}/redact`, {
    method: 'POST',
    body: JSON.stringify({ regions }),
  });
